import type { ApiMagentoPriceTier, MagentoPrice, MagentoPriceTier, MagentoStockStatus } from "@jti/magento-types";

import type { MultiselectQuantity } from "../MultiselectQuantity";
import type { CamelToSnakeCaseNested } from "../utility-types/notation-utility.types";

import type { ProductGalleryAsset } from "./ProductGalleryAsset.type";
import type { Category, MediaGalleryItem, SticksMultiPackProduct } from "./SticksProductDetails.types";

export type DynamicGalleryLayout =
  | "layout-sticks-only"
  | "layout-2-products"
  | "layout-3-products"
  | "layout-3-products-1-horizontal"
  | "without-layout";

export type DynamicGalleryConfig = {
  layout: DynamicGalleryLayout;
  items: DynamicGalleryItem[];
};

export type DynamicGalleryItem = {
  itemNumber: number;
  placeholderImage: string;
};

export type DynamicGalleryCollection = {
  placeholderImage: string | string[];
  optionId: number;
  itemNumber: number;
  sicksPlaceHolderImage?: string;
};

export type ConfigurableOption = {
  list: ConfigurableProduct[];
  categoryName: string;
};

export type MultiPackProduct = {
  sku: string;
  name: string;
  pdpUrl?: string;
  price?: string;
  shortDescription?: string;
  multiPackDescription?: string;
  multiPackName?: string;
};

export type AEMFlavourAttribute = {
  id: string;
  name: string;
  sort_order: string;
  value: string;
};

export type FlavourDefinition = {
  id: string;
  description: string;
  name: string;
  attributes_custom_: AEMFlavourAttribute[];
};

export type FlavourDetails = FlavourDefinition[];

export type AemBundleProductConfiguration = {
  name: string;
  sku: string;
  updatedAt: string | null;
  color: string | number | null;
  id?: number;
  flavor: string | number | null;
  flavorLabel: string | null;
  flavorDetails: FlavourDetails | null;
  productBrand: string | null;
  colorName?: string | null;
  description: string;
  shortDescription?: string;
  tradeIn?: boolean;
  hasRegistrationConsent?: boolean;
  mandatoryPositiveConsent?: boolean;
  nonTradeInSku?: string | null;
  customBuyCtaLabelPDP?: string | null;
  customBuyCtaLabelPLP?: string | null;
  categories: string[] | null;
  thumbnail?: {
    url: string;
  };
  image: string | null;
  mediaGallery: ProductGalleryAsset[] | null;
  newFromDate: string | null;
  newToDate: string | null;
  tooltipInfo?: string;
  metaDescription: string | null;
  metaTitle: string | null;
  urlKey: string | null;
  multiPackLabel: string | null;
  multiPackEnabled: boolean;
  multiPackDescription: string | null;
  multiPackProducts: MultiPackProduct[] | null;
  variants: string[] | null;
  configurableOptions: string[] | null;
  frontendAttributes: Record<string, string[]> | null;
  bundle: boolean;
  productBundleConfiguration: null;
  specification: Record<string, any> | null;
  tierPrices: string[] | null;
};

export type BundleConfigOption = {
  swatchData: string | null;
  id: number;
  isDefault: boolean;
  label: string;
  position: number | null;
  price: number | null;
  priceType: string | null;
  quantity: number;
  product: AemBundleProductConfiguration;
  isInStock?: boolean;
};

export type SupportedBundleItemsTypes = "select" | "multi";

export type BundleConfigItem = {
  optionId: number;
  sku: string;
  type: SupportedBundleItemsTypes;
  title: string;
  options: BundleConfigOption[];
  optionsString?: string;
  qty: number;
  allOptions: BundleConfigOption[];
  allOptionsString?: string;
};

export type BundleConfig = Array<BundleConfigItem>;

export type DeviceRegistrationConfiguration = {
  theme?: "light" | "dark";
  registerDeviceImgDesktop?: string;
  registerDeviceImgMobile?: string;
  registerDeviceImgAlt?: string;
  registerDeviceDialogTitle?: string;
  registerDeviceDialogDescription?: string;
  registerDeviceAccordionTitle?: string;
  registerDeviceAccordionButtonUrl?: string;
  registerDeviceAccordionButtonText?: string;
  cancelDeviceRegistrationButtonText?: string;
  confirmDeviceRegistrationButtonText?: string;
  isDeviceCodeFormatLabelVisible?: boolean;
  is12DeviceCodeLength?: boolean;
  is15DeviceCodeLength?: boolean;
  is16DeviceCodeLength?: boolean;
  accordionImageOne?: string;
  accordionImageOneAlt?: string;
  accordionImageOneDescription?: string;
  accordionImageTwo?: string;
  accordionImageTwoAlt?: string;
  accordionImageTwoDescription?: string;
};

export type ProductDetailsData = {
  isCommerceDisabled: boolean;
  bundlesConfig: BundleConfig;
  sku: string;
  categories: Array<{ id: string; name: string }>;
  currentVariantId: string;
  currentVariantSku: string;
  configurableOptions: ConfigurableOption[];
  knowMoreUrl: string;
  description: string;
  shortDescription?: string;
  customBuyCtaLabelPDP?: string | null;
  customBuyCtaLabelPLP?: string | null;
  multiPackDescription: string;
  multiPackName: string;
  isAuthorMode: boolean;
  isBundle: boolean;
  isTrial: boolean;
  multiPackProducts: MultiPackProduct[];
  isMultiPackEnabled: boolean;
  hideCta: boolean;
  hasStrikethroughPrice: boolean;
  tierPrices?: ApiMagentoPriceTier[];
  hasBadge?: boolean;
  hasDiscountBadge: boolean;
  overwriteBadgeText?: string;
  badgeText?: string;
  productName: string;
  isDynamicGalleryEnabled: boolean;
  dynamicGallery: DynamicGalleryConfig | null;
  videoGallery: ProductGalleryAsset[];
  galleryAssets: ProductGalleryAsset[];
  isTradeIn?: boolean;
  customBuyCtaLabel?: string;
  nonTradeInSku: string | "";
  trustpilot: {
    isEnabled: boolean;
  };
  productCta: {
    label: string;
    url: string;
  };
  hidePrice?: boolean;
  hasRegistrationConsent?: boolean;
  mandatoryPositiveConsent?: boolean;
  // consentSectionConfiguration is null when content fragment asset is not
  // present for current tenant + language combination
  consentSectionConfiguration?: ConsentSectionConfiguration | null;
  isTradeInDisclaimerVisible?: boolean;
  tradeInDisclaimerData?: {
    title: string;
    description: string;
    ctaText: string;
    ctaLink: string;
  };
  deviceRegistrationConfiguration?: DeviceRegistrationConfiguration;
  dynamicGalleryUrls?: Record<string, string>;
  multipacksQuantities?: { option_id: number; qty: number }[];
};

export type ConsentSectionConfiguration = {
  consentSectionTitle: string;
  consentSectionSubtitle?: string;
  shortHtml: string;
  longHtml?: string;
  agreeRadioLabel: string;
  rejectRadioLabel: string;
  showMoreBenefitsButtonLabel?: string;
  showLessBenefitsButtonLabel?: string;
  errorText?: string;
};

export type ProductInterface = { __typename: string } & any;

export type ProductPriceType = {
  price?: MagentoPrice;
  finalPrice?: MagentoPrice;
};

export type ConfigurableProduct = {
  id: string;
  label: string;
  sku: string;
  swatchData: string;
  url: string;
};

export interface BlockOptionsType {
  isDefault?: boolean;
  id: string;
  isInStock: boolean;
  product: {
    thumbnail: {
      url: string;
    };
  };
  type: string;
}

export type SelectedBundleItem = {
  value: string[];
  quantity?: number;
  quantities: CamelToSnakeCaseNested<MultiselectQuantity>[];
  id: string;
  isInStock: boolean;
  stockStatus?: MagentoStockStatus;
};

export type BlocksModelElementType = {
  product: BlockOptionsType;
  data: {
    value: string | string[];
    quantity: number;
    isInStock?: boolean;
    stockStatus?: MagentoStockStatus;
  };
};

type Qantities = {
  selection_id: string;
  qty: number;
};

export type LastSelectedElement = {
  optionId: number;
  type: "multi" | "select";
  productId?: number;
  quantities?: Qantities[];
};

export type StockStatusElements = Record<string, MagentoStockStatus>;

export type ProductPrice = {
  price?: MagentoPrice;
  finalPrice: MagentoPrice;
};

export type ProductDetailsProductData = {
  id: number;
  sku: string;
  name: string;
  color: string | number | null;
  colorName: string | null;
  flavor: string;
  flavorLabel: string | null;
  flavorDetails: FlavourDetails | null;
  description: string;
  shortDescription?: string;
  mediaGallery: MediaGalleryItem[];
  categories: Category[];
  thumbnail: { url: string };
  image: { url: string };
  urlKey: string;
  multiPackEnabled: boolean;
  multiPackLabel: string | null;
  multiPackDescription: string | null;
  multiPackProducts: SticksMultiPackProduct[];
  frontendAttributes: Record<string, string[]>;
  bundle: boolean;
  specification: Record<string, string>;
  updatedAt: string;
  newFromDate: string | null;
  newToDate: string | null;
  tooltipInfo: string;
  metaDescription: string | null;
  metaTitle: string | null;
  variants: string[];
  configurableOptions: ConfigurableOption[];
  productBundleConfiguration: { items: BundleConfigItem[] } | null;
  tierPrices: MagentoPriceTier[];
  regularPrice?: MagentoPrice;
  tradeIn?: boolean;
  hasRegistrationConsent?: boolean;
  mandatoryPositiveConsent?: boolean;
  nonTradeInSku?: string | null;
  customBuyCtaLabelPDP?: string | null;
  customBuyCtaLabelPLP?: string | null;
};

export type FlavorsSelection = { [key: string]: number };
